import {
  isCreateChildTicketTicketWorkflow,
  WORKPLACE_REQUEST_WORKFLOW_ACTIONS,
  WorkplaceRequestCreateChildTicketConfig,
  WorkplaceRequestTicketWorkflow,
  WorkplaceRequestWorkflow,
  WorkplaceRequestWorkflowRun
} from "@gettactic/api";

/**
 * The follow-up requests a type spawns when one of its requests is approved —
 * read off the `tickets:create_child_ticket` workflows, so both the approver
 * (who picks which ones to run) and everyone reading the request afterwards
 * (who sees which ones were deliberately skipped) describe them the same way.
 *
 * Skipping is per workflow, not per child: a workflow that creates two requests
 * creates both or neither, which is why a follow-up here is a workflow with the
 * requests it would file under it.
 */

/** One request a workflow would create. */
export type FollowUpChild = {
  /** The request type it is filed under. */
  typeId: string;
  /** The title the workflow gives it, or `null` to inherit the type's name. */
  title: string | null;
};

export type FollowUp = {
  workflowId: string;
  /** The workflow's own name, or `null` when it was never given one. */
  name: string | null;
  children: FollowUpChild[];
  /**
   * The workflow defers its requests to a date (`run_at`) instead of filing
   * them the moment the approval lands. The approver's checkbox means the same
   * either way, but "approving creates this now" and "approving schedules it"
   * are different promises, so the two are labelled differently.
   */
  isDeferred: boolean;
};

/**
 * What core records on a run the approver chose not to trigger. Matching on it
 * separates a deliberate skip from a workflow that skipped itself (a value it
 * needed wasn't set), which is not the approver's doing and reads differently.
 */
export const SKIPPED_BY_APPROVER = "skipped by approver";

const toChildren = (
  config: WorkplaceRequestCreateChildTicketConfig | undefined
): FollowUpChild[] =>
  (config?.children ?? []).map((child) => ({
    typeId: child.target_ticket_type_id,
    title: child.name?.trim() ? child.name.trim() : null
  }));

/**
 * The follow-ups a pending request would create on approval, from the resolved
 * workflows core returns for it (`GET /v1/tickets/{id}/workflows`). Those ids
 * are the ones `skip_workflow_ids` accepts.
 */
export const resolveFollowUps = (
  workflows: WorkplaceRequestTicketWorkflow[]
): FollowUp[] =>
  workflows.filter(isCreateChildTicketTicketWorkflow).map((workflow) => ({
    workflowId: workflow.id,
    name: workflow.name,
    children: toChildren(workflow.resolved),
    isDeferred: !!workflow.resolved?.run_at
  }));

/**
 * The follow-ups an approver deliberately left out, matched from the request's
 * runs back to the type's workflows — the run itself only names the workflow,
 * and only its config says which requests it would have created.
 *
 * A workflow deleted since the approval simply drops out: there is nothing left
 * to describe it with.
 */
export const resolveSkippedFollowUps = (
  runs: WorkplaceRequestWorkflowRun[] | undefined,
  typeWorkflows: WorkplaceRequestWorkflow[] | undefined
): FollowUp[] => {
  const skippedRuns = (runs ?? []).filter(
    (run) => run.status === "skipped" && run.error === SKIPPED_BY_APPROVER
  );
  if (skippedRuns.length === 0) return [];

  const workflowById = new Map(
    (typeWorkflows ?? [])
      .filter(
        (workflow) =>
          workflow.action ===
          WORKPLACE_REQUEST_WORKFLOW_ACTIONS.createChildTicket
      )
      .map((workflow) => [workflow.id, workflow])
  );

  return skippedRuns.flatMap((run) => {
    const workflow = workflowById.get(run.workflow.id);
    const config = workflow?.config;
    if (!workflow || !config || !("children" in config)) return [];
    return [
      {
        workflowId: workflow.id,
        name: workflow.name,
        children: toChildren(config),
        isDeferred: !!config.run_at
      }
    ];
  });
};
